import { useCallback } from "react";
import { WebSocketMessage, WebSocketMessageType } from "../../providers/web_sockets_provider";
import { useAnalytics } from "../../hooks/analytics";
import { Annotation, AnnotationType, ConversationTurn, ConversationTurnRouting } from "../../types";
import { isExactNear } from "../../lib/routing";
import { AnalyticsEvent } from "../../analytics/track";
import { setSearchInteractionSuccessful } from "../../analytics/interactions";
import { ResultsData } from "./use_conversations_search";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Types

export type ConversationsCreateData = {
  content?: string;
  questions?: Annotation[];
  articles?: Annotation[];
  routing?: ConversationTurnRouting;
  complete: boolean;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Formatting Helpers

const shouldShowContent = (routing?: ConversationTurnRouting): boolean => {
  return isExactNear(routing);
};

const getQuestions = (conversationTurn?: ConversationTurn): Annotation[] => {
  if (!conversationTurn) return [];

  // Get the first five related questions and/or suggestions
  return conversationTurn.content.annotations
    .filter((annotation) => [AnnotationType.RelatedQuestion, AnnotationType.Suggestion].includes(annotation.type))
    .slice(0, 5);
};

const getArticles = (conversationTurn?: ConversationTurn): Annotation[] => {
  if (!conversationTurn) return [];

  // Get the first five citations
  return conversationTurn.content.annotations
    .filter((annotation) => annotation.type === AnnotationType.Citation)
    .slice(0, 5);
};

export function useConversationsMessageHandler() {
  const { track } = useAnalytics();

  const getUpdateResponse = useCallback((message: WebSocketMessage): ConversationsCreateData | undefined => {
    const { routing, content } = message;
    if (shouldShowContent(routing) && content?.length) {
      // Start streaming the content to the UI
      return { content: content.trim(), routing, complete: false };
    }

    // No content to show, keep showing loading until the response is complete
    return undefined;
  }, []);

  const getCompletedResponse = useCallback(
    (message: WebSocketMessage): ConversationsCreateData | undefined => {
      const { query, conversation_turn: conversationTurn } = message;
      const routing = conversationTurn?.routing;
      const content = shouldShowContent(routing) ? conversationTurn?.content?.value : undefined;

      const questions = getQuestions(conversationTurn);
      const articles = getArticles(conversationTurn);

      track(AnalyticsEvent.SEARCH_RESULTS_GENERATED, {
        query,
        query_type: routing,
        query_results: {
          answer: content,
          questions,
          articles,
        },
      });

      if (isExactNear(routing)) {
        setSearchInteractionSuccessful();
      }

      return {
        content,
        questions,
        articles,
        routing,
        complete: true,
      };
    },
    [track],
  );

  const getFailedResponse = useCallback((message: WebSocketMessage): Error | undefined => {
    return new Error(message.error);
  }, []);

  const getResponse = useCallback(
    (message: WebSocketMessage): ResultsData | undefined => {
      switch (message.message) {
        case WebSocketMessageType.RequestUpdate: {
          // We don't want to revert to loading if we don't have content, so don't update
          // the state at all if we don't have content (can happen when a near query is streaming)
          const data = getUpdateResponse(message);
          if (data) {
            return { data, error: undefined };
          }
          return undefined;
        }
        case WebSocketMessageType.RequestCompleted:
          return { data: getCompletedResponse(message), error: undefined };
        case WebSocketMessageType.RequestFailed:
          return { data: undefined, error: getFailedResponse(message) };
        default:
          return { data: undefined, error: undefined };
      }
    },
    [getCompletedResponse, getFailedResponse, getUpdateResponse],
  );

  return { getResponse };
}
